import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { AbstractControl, FormControl } from '@angular/forms';
import { Observable, Subject } from 'rxjs';
import { BrowserManagerDataDispatcher } from '../../../browser-manager-data/browser-manager-data.dispatcher';
import {
  ILocalStorageKeyValue,
  LocalStorageService,
} from '../../../browser-manager-data/local-storage.service';
import { IConfirmDialogResult } from '../../../dialog/components/confirm-dialog/confirm-dialog.interface';
import { DialogActionsEnum, IDialogTrigger } from '../../../dialog/dialog.data';
import { DialogService } from '../../../dialog/dialog.service';
import { CONVERT_TO_INVOICE_DONT_ASK_ME_AGAIN_KEY } from '../../../document-data/document-data.data';
import { Document } from '../../../document-data/document.model';
import { I2gNgTrackingActionBuilder } from 'i2g-ng-tracking';
import {
  ITrackingAction,
  TrackingDialogNamesEnum,
  TrackingScreenNamesEnum,
} from '../../../tracking/tracking.data';
import { filter, map, takeUntil } from 'rxjs/operators';

export const DIALOG_TYPE = 'convert-to-invoice';

export interface IConvertToInvoiceDialogData {
  document: Document;
}

@Component({
  selector: 'app-convert-to-invoice-dialog',
  templateUrl: './convert-to-invoice-dialog.component.html',
  styleUrls: ['./convert-to-invoice-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConvertToInvoiceDialogComponent
  implements OnInit, OnChanges, OnDestroy {
  @Input() document: Document;
  @Input() trackingScreenName: TrackingScreenNamesEnum;

  @Output() onConvert: EventEmitter<Document> = new EventEmitter<Document>();

  private _destroyed: Subject<boolean> = new Subject<boolean>();

  convertToInvoiceDialogName: TrackingDialogNamesEnum;
  dialogType: string;
  convertToInvoicePrimaryTapped: ITrackingAction;
  convertToInvoiceSecondaryTapped: ITrackingAction;
  dontAskMeAgainControl: AbstractControl;
  dontAskMeAgainChecked$: Observable<boolean>;

  constructor(
    private _trackingActionBuilder: I2gNgTrackingActionBuilder,
    private _localStorageService: LocalStorageService,
    private _browserManagerDataDispatcher: BrowserManagerDataDispatcher,
    private _dialogService: DialogService
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes.trackingScreenName && this.trackingScreenName) {
      this.convertToInvoicePrimaryTapped = this._trackingActionBuilder.buildButtonTapped(
        'yes_convert_to_invoice',
        this.trackingScreenName
      );
      this.convertToInvoiceSecondaryTapped = this._trackingActionBuilder.buildButtonTapped(
        'cancel',
        this.trackingScreenName
      );
    }
  }

  ngOnInit() {
    this.dontAskMeAgainChecked$ = this._localStorageService
      .onKeyChange(CONVERT_TO_INVOICE_DONT_ASK_ME_AGAIN_KEY)
      .pipe(map(({ value }: ILocalStorageKeyValue) => Boolean(value)));

    this.dialogType = DIALOG_TYPE;
    this.convertToInvoiceDialogName =
      TrackingDialogNamesEnum.confirm_convert_to_invoice;
    this.dontAskMeAgainControl = new FormControl(true);

    this._dialogService.dialogTriggers$
      .pipe(
        filter(
          ({ action, type }: IDialogTrigger) =>
            action !== DialogActionsEnum.close && type === this.dialogType
        ),
        takeUntil(this._destroyed)
      )
      .subscribe(({ data }: IDialogTrigger) => {
        const { document }: IConvertToInvoiceDialogData = data;

        this.document = document || this.document;
      });
  }

  convertToInvoice({ result }: IConfirmDialogResult) {
    if (result) {
      if (this.dontAskMeAgainControl.value) {
        this._browserManagerDataDispatcher.setLocalStorageItem(
          CONVERT_TO_INVOICE_DONT_ASK_ME_AGAIN_KEY,
          true
        );
      }

      this.onConvert.emit(this.document);
    }
  }

  ngOnDestroy() {
    this._destroyed.next(true);
    this._destroyed.unsubscribe();
  }
}
